import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  makeStyles,
  Grid,
  Button,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Tooltip,
} from "@material-ui/core";

import { Controller, useForm } from "react-hook-form";
import { FileInput } from "@ofqual/portal-components/components/FileInput";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { Error } from "../../../common/Error";
import React, { useEffect, useState } from "react";
import { sendMessage } from "../../../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { buildDefinitions } from "../buildDefinitions";
import {
  Activity,
  ActivityFeedItem,
  ActivityFeedItemDefinition,
  ActivitySegmentHeader,
} from "@ofqual/portal-activity-feed";
import { InformationRequest } from "../../../../domain/InformationRequest";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { feedSelector } from "../../../../redux/selectors";
import { formatAsTodayYesterdayOrDate } from "@ofqual/portal-core/core/utils/dateUtils";
import {
  Segment,
  segmentList,
} from "@ofqual/portal-core/core/utils/listHelpers";
import { requestTypeLookupService } from "../../../../services";
import { ActivityType } from "../../../../domain/ActivityType";
import WarningIcon from "@material-ui/icons/Warning";
import { LegalDisclaimerMessageDialog } from "../../recipients/dialogs/LegalDisclaimerDialog";

const useStyles = makeStyles((theme) => ({
  actions: {
    padding: 15,
  },
}));

export const SendMessageDialog: React.FC<{
  onClose: () => void;
  informationRequest: InformationRequest;
  resourceId: string;
}> = ({ onClose, informationRequest, resourceId }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { handleSubmit, control, errors } = useForm({
    defaultValues: {
      message: null,
      files: [],
    },
  });

  const handleCreateAction = (data: { files: File[]; message: string }) => {
    dispatch(
      sendMessage(informationRequest.id, resourceId, data.files, data.message)
    );
    onClose();
  };

  const [requestTypeLookups] = useLookup(requestTypeLookupService);

  // build our definintions cast to any because ActivityFeedItem is a little fussy
  const definitions: ActivityFeedItemDefinition<any>[] = buildDefinitions(
    () => {},
    informationRequest,
    requestTypeLookups
  );

  // create a list of activities filtered by our message types
  const Values = (): Activity[] => {
    let items: Activity[] = [];
    const activity = useSelector(feedSelector);
    const value = activity[resourceId];
    if (value && value.hasData && value.status === "LOADED") {
      items = value.response.data.filter(
        (item) =>
          item.activityType ===
            ActivityType.InformationRequestRecipientMessage ||
          item.activityType === ActivityType.InformationRequestMessageSent ||
          item.activityType === ActivityType.InformationRequestMessageSentAO ||
          item.activityType ===
            ActivityType.InformationRequestRecipientMessageAO ||
          item.activityType ===
            ActivityType.InformationRequestRespondToMessageAction
      );
    }
    return items;
  };

  const [displayLegalDisclaimer, setDisplayLegalDisclaimer] = useState(false);
  const onCloseLegalDisclaimer = () => {
    setDisplayLegalDisclaimer(false);
  };
  const onLegalDisclaimerClick = () => {
    setDisplayLegalDisclaimer(true);
  };

  // create a date segmented list
  const segmentedActivities = segmentList(
    Values(),
    (item) =>
      /// ... fall back to a standard by-date segmentation.
      formatAsTodayYesterdayOrDate(item.createdDate) || ""
  );
  //Scroll to bottom of this reference activity feed element
  const activityFeedEl = React.createRef<HTMLDivElement>();
  const [segmentedActivityItems, setSegmentedActivityItems] = useState<
    Segment<Activity>[]
  >([]);
  useEffect(() => {
    if (activityFeedEl.current) {
      activityFeedEl.current.scrollIntoView({
        block: "end",
        behavior: "smooth",
      });
    }
  }, [segmentedActivityItems, activityFeedEl]);

  // i don't like this I am using a one off use effect to trigger another and is this a way down inside
  useEffect(() => {
    setSegmentedActivityItems((segmentedActivityItems) => [
      ...segmentedActivities,
    ]);
  }, []);

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle data-test="activity-title">Send Message</DialogTitle>
      <Divider />
      <DialogContent style={{ minHeight: "380px" }}>
        <form>
          <Grid item xs={12}>
            <ListItem>
              <ListItemIcon>
                <WarningIcon color="secondary" />
              </ListItemIcon>
              <ListItemText>
                If you are unable to provide the information or cannot provide
                it within the specified timescale you should notify Ofqual as
                soon as possible, explaining why it cannot be provided.
              </ListItemText>
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <WarningIcon color="secondary" />
              </ListItemIcon>
              <ListItemText>
                Unless specifically requested you should redact personal
                information from any documents provided.
              </ListItemText>
            </ListItem>
          </Grid>
          <p></p>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="message"
                  rules={{ required: true }}
                  render={({ onChange, value }) => (
                    <>
                      <label>Message:</label>
                      <MarkdownEditor
                        dataTest="send-message-freetext-field"
                        onChange={onChange}
                        value={value}
                      />
                    </>
                  )}
                />
              </Grid>
              {errors.message && (
                <Error data-test="send-message-error">
                  This field is required
                </Error>
              )}
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="files"
                as={
                  <FileInput
                    multiple={true}
                    dataTest="send-message-select-files-button"
                  />
                }
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <ListItem>
          <ListItemIcon>
            <WarningIcon color="secondary" />
          </ListItemIcon>
          <Tooltip
            title="Click to see full guidance notes"
            aria-label="See-full-guidance-notes"
          >
            <ListItemText
              onClick={onLegalDisclaimerClick}
              style={{ textDecoration: "underline" }}
            >
              Please note the following important information before sending
            </ListItemText>
          </Tooltip>
        </ListItem>

        <Button
          onClick={onClose}
          color="primary"
          data-test="close-action-dialog"
        >
          Close
        </Button>
        <Button
          onClick={handleSubmit(handleCreateAction)}
          color="primary"
          data-test="confirm-create-action-button"
        >
          Send
        </Button>
      </DialogActions>

      <Divider />
      <p></p>
      <DialogContent>
        <div ref={activityFeedEl}>
          {segmentedActivityItems.map((segment) => (
            <div key={segment.label}>
              <ActivitySegmentHeader label={segment.label} />
              {segment.data.map((item) => (
                <ActivityFeedItem
                  key={item.id}
                  activity={item}
                  definition={definitions.find(
                    (definition) =>
                      definition.activityType === item.activityType
                  )}
                />
              ))}
            </div>
          ))}
        </div>
      </DialogContent>
      <LegalDisclaimerMessageDialog
        open={displayLegalDisclaimer}
        onClose={onCloseLegalDisclaimer}
      />
    </Dialog>
  );
};
